/*
  Header behaviour Empire's own JavaScript never gave this build, because it
  only attaches to `static-header` and this theme does not render that
  section (Theme/build-v2/README.md, tu-header.liquid). Two pieces:

    TuMegaNav  the hover/click/keyboard panel on nav doors with children.
    TuSearch   predictive search: fetch, debounce, race-proof render.

  Shared outside-pointerdown watcher. Empire binds its dismiss listener
  straight to `document.body` per header instance and never removes it, so
  every `shopify:section:load` in the theme editor adds one more (found
  2026-09-04, reading NavDesktopParent/LiveSearch for this build). One
  listener for the module's whole lifetime, a registry that prunes itself
  when a watched element leaves the DOM, fixes that for both classes below.
*/
(function () {
  const outsideWatchers = new Set();
  let outsideBound = false;

  function watchOutside(container, onOutside) {
    const entry = { container, onOutside };
    outsideWatchers.add(entry);
    if (!outsideBound) {
      outsideBound = true;
      document.addEventListener('pointerdown', (e) => {
        outsideWatchers.forEach((w) => {
          if (!w.container.isConnected) {
            outsideWatchers.delete(w);
            return;
          }
          if (!w.container.contains(e.target)) w.onOutside(e);
        });
      });
    }
    return () => outsideWatchers.delete(entry);
  }

  // -- TuMegaNav -------------------------------------------------------------

  class TuMegaNav {
    constructor(nav) {
      this.nav = nav;
      this.triggers = Array.from(nav.querySelectorAll('[data-tu-mega-trigger]'));
      this.dim = nav.querySelector('[data-tu-dim]');
      this.openTrigger = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.hoverEnabled = nav.dataset.tuMegaHover !== 'false';
      this.hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.desktopMQ = window.matchMedia('(min-width: 750px)');
      this.desktopMQ.addEventListener('change', () => this.closeAll());

      this.triggers.forEach((trigger) => this.bindTrigger(trigger));
      this.nav.addEventListener('keydown', (e) => this.onKeydown(e));
      watchOutside(this.nav, () => this.closeAll());
      // The dimmer sits inside the nav, so the outside watcher never sees a
      // click on it; without hover (a click-opened panel on a touch laptop)
      // that click has to close the panel itself (audit 2026-09-04).
      if (this.dim) this.dim.addEventListener('click', () => this.closeAll());
    }

    panelFor(trigger) {
      const id = trigger.getAttribute('aria-controls');
      return id ? document.getElementById(id) : null;
    }

    hoverActive() {
      return this.hoverEnabled && this.hoverMQ.matches;
    }

    bindTrigger(trigger) {
      const panel = this.panelFor(trigger);
      if (!panel) return;

      // First activation (click, tap, or Enter — a browser turns Enter on a
      // focused <a> into a click on its own) opens the door; a second
      // activation, while it is already the open door, is left alone and
      // navigates normally.
      const activate = (e) => {
        if (trigger.getAttribute('aria-expanded') === 'true') return;
        e.preventDefault();
        this.open(trigger, panel);
      };
      trigger.addEventListener('click', activate);
      trigger.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          activate(e);
        }
      });

      trigger.addEventListener('mouseenter', () => this.onHoverEnter(trigger, panel));
      trigger.addEventListener('mouseleave', () => this.onHoverLeave());
      panel.addEventListener('mouseenter', () => this.onHoverStay());
      panel.addEventListener('mouseleave', () => this.onHoverLeave());
    }

    onHoverEnter(trigger, panel) {
      if (!this.hoverActive()) return;
      clearTimeout(this.closeTimer);
      if (this.openTrigger === trigger) return;
      if (this.openTrigger) {
        // Entering another door swaps at once — no open or close delay.
        clearTimeout(this.openTimer);
        this.closeImmediate(this.openTrigger);
        this.open(trigger, panel);
        return;
      }
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => this.open(trigger, panel), 200);
    }

    onHoverStay() {
      if (!this.hoverActive()) return;
      clearTimeout(this.closeTimer);
    }

    onHoverLeave() {
      if (!this.hoverActive()) return;
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeAll(), 400);
    }

    open(trigger, panel) {
      if (this.openTrigger && this.openTrigger !== trigger) this.closeImmediate(this.openTrigger);
      this.openTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      // rAF so `hidden` clears before the transition class applies — the
      // browser needs one paint between them or it skips straight to the
      // end state and the panel just appears.
      requestAnimationFrame(() => panel.classList.add('is-open'));
      if (this.dim && this.desktopMQ.matches) this.dim.hidden = false;
    }

    closeImmediate(trigger) {
      const panel = this.panelFor(trigger);
      trigger.setAttribute('aria-expanded', 'false');
      if (panel) {
        panel.classList.remove('is-open');
        panel.hidden = true;
      }
    }

    closeAll() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.openTrigger) this.closeImmediate(this.openTrigger);
      this.openTrigger = null;
      if (this.dim) this.dim.hidden = true;
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.openTrigger) {
          const trigger = this.openTrigger;
          this.closeAll();
          trigger.focus();
          e.preventDefault();
        }
        return;
      }

      const target = e.target;
      if (target.matches && target.matches('[data-tu-mega-trigger]')) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          const i = this.triggers.indexOf(target);
          const next = e.key === 'ArrowRight' ? this.triggers[i + 1] : this.triggers[i - 1];
          if (next) {
            next.focus();
            e.preventDefault();
          }
          return;
        }
        if (e.key === 'ArrowDown') {
          const panel = this.panelFor(target);
          if (!panel) return;
          if (target.getAttribute('aria-expanded') !== 'true') this.open(target, panel);
          const first = panel.querySelector('.tu-mega__col a, .tu-mega__shopall');
          if (first) {
            first.focus();
            e.preventDefault();
          }
        }
        return;
      }

      const panel = target.closest && target.closest('.tu-mega');
      if (!panel) return;
      const columns = Array.from(panel.querySelectorAll('.tu-mega__col')).map((col) =>
        Array.from(col.querySelectorAll('a'))
      );
      let ci = -1;
      let ri = -1;
      columns.forEach((col, i) => {
        const j = col.indexOf(target);
        if (j > -1) {
          ci = i;
          ri = j;
        }
      });
      if (ci === -1) return;

      if (e.key === 'ArrowDown') {
        const next = columns[ci][ri + 1];
        if (next) {
          next.focus();
          e.preventDefault();
        }
      } else if (e.key === 'ArrowUp') {
        const prev = columns[ci][ri - 1];
        if (prev) {
          prev.focus();
          e.preventDefault();
        } else if (this.openTrigger) {
          this.openTrigger.focus();
          e.preventDefault();
        }
      } else if (e.key === 'ArrowRight') {
        const col = columns[ci + 1];
        const t = col && col[Math.min(ri, col.length - 1)];
        if (t) {
          t.focus();
          e.preventDefault();
        }
      } else if (e.key === 'ArrowLeft') {
        const col = columns[ci - 1];
        const t = col && col[Math.min(ri, col.length - 1)];
        if (t) {
          t.focus();
          e.preventDefault();
        }
      }
    }
  }

  // -- TuDrawer ----------------------------------------------------------
  // The mobile hamburger drawer (nav-doors-plan.md). Every level is
  // already server-rendered by snippets/tu-drawer-panel.liquid, `hidden`
  // except the current one -- opening a level here is nothing but moving
  // that attribute and pushing/popping a small stack for the back button.
  // No fetch, ever.

  class TuDrawer {
    constructor(drawer) {
      this.drawer = drawer;
      this.toggle = document.querySelector(
        `[data-tu-drawer-toggle][aria-controls="${drawer.id}"]`
      );
      this.panel = drawer.querySelector('.tu-drawer__panel');
      this.scrim = drawer.querySelector('[data-tu-drawer-scrim]');
      this.closeBtn = drawer.querySelector('[data-tu-drawer-close]');
      this.backBtn = drawer.querySelector('[data-tu-drawer-back]');
      this.titleEl = drawer.querySelector('[data-tu-drawer-title]');
      this.body = drawer.querySelector('[data-tu-drawer-body]');
      const root = this.body && this.body.querySelector('[data-tu-drawer-level]');
      if (!this.toggle || !this.panel || !this.body || !root) return;

      this.stack = [
        { level: root, opener: null, title: this.titleEl ? this.titleEl.textContent.trim() : 'Menu' },
      ];
      this.isOpen = false;
      this.transitionToken = 0;
      this.desktopMQ = window.matchMedia('(min-width: 750px)');
      this.boundKeydown = (e) => this.onKeydown(e);

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.backBtn) this.backBtn.addEventListener('click', () => this.back());

      // First activation of a `[data-tu-drawer-open]` row opens the next
      // level; a second activation, while it is already open, is left
      // alone and navigates -- same convention as TuMegaNav's trigger,
      // ported from hover to drill-down.
      this.body.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-tu-drawer-open]');
        if (!opener) return;
        if (opener.getAttribute('aria-expanded') === 'true') return;
        e.preventDefault();
        this.openLevel(opener);
      });

      // Grown past the drawer breakpoint with it open: close it and let
      // the desktop nav (never hidden there) stand in its place.
      this.desktopMQ.addEventListener('change', (e) => {
        if (e.matches) this.close(true);
      });
    }

    focusables() {
      return Array.from(this.panel.querySelectorAll('a[href], button:not([disabled])')).filter(
        (el) => el.offsetParent !== null
      );
    }

    focusFirst(level) {
      const first = level.querySelector('a[href], button:not([disabled])');
      if (first) first.focus();
    }

    updateHead() {
      const top = this.stack[this.stack.length - 1];
      if (this.titleEl) this.titleEl.textContent = top.title;
      if (this.backBtn) this.backBtn.hidden = this.stack.length <= 1;
    }

    openLevel(opener) {
      const id = opener.getAttribute('aria-controls');
      const next = id && document.getElementById(id);
      if (!next) return;
      const cur = this.stack[this.stack.length - 1];
      opener.setAttribute('aria-expanded', 'true');
      const title = next.dataset.title || opener.textContent.trim();
      this.stack.push({ level: next, opener, title });
      this.updateHead();
      this.slideLevels(cur.level, next, 'forward');
    }

    back() {
      if (this.stack.length <= 1) return;
      const top = this.stack.pop();
      if (top.opener) top.opener.setAttribute('aria-expanded', 'false');
      const cur = this.stack[this.stack.length - 1];
      this.updateHead();
      this.slideLevels(top.level, cur.level, 'back', top.opener);
    }

    // Task 4 (2026-09-12), client: "add animations on the hamburger menu on
    // the phone." Levels used to swap by `hidden` alone -- instant, no sense
    // of hierarchy. Forward (drilling in) pushes the current level left as
    // the next one enters from the right; Back pulls the reverse. Both
    // levels are `position: absolute; inset: 0` inside `.tu-drawer__body`
    // (overflow: hidden), so they can sit on top of each other mid-slide.
    // `hidden` still gates tab order (TuDrawer.focusables()) and is the
    // fallback path — same transitionend + setTimeout belt-and-braces
    // pattern as TuDrawer.close(), for a dropped transitionend.
    slideLevels(outLevel, inLevel, dir, focusReturnEl) {
      this.levelToken = (this.levelToken || 0) + 1;
      const token = this.levelToken;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const restoreFocus = () => {
        // Back returns focus to the row that opened the level being closed
        // (unchanged from the pre-animation behaviour); drilling in focuses
        // the first row of the level just revealed, same as before too.
        if (focusReturnEl) focusReturnEl.focus();
        else this.focusFirst(inLevel);
      };

      if (reduced) {
        outLevel.hidden = true;
        outLevel.style.transform = '';
        inLevel.style.transform = '';
        inLevel.hidden = false;
        restoreFocus();
        return;
      }

      const outTo = dir === 'forward' ? '-100%' : '100%';
      const inFrom = dir === 'forward' ? '100%' : '-100%';

      inLevel.style.transition = 'none';
      inLevel.style.transform = `translateX(${inFrom})`;
      inLevel.hidden = false;
      // Force a layout flush so the browser paints the start position before
      // the transition below is allowed to run -- the same reason
      // TuMegaNav.open() and TuDrawer.open() each need one rAF between
      // clearing `hidden` and adding the class/style that animates.
      // eslint-disable-next-line no-unused-expressions
      inLevel.getBoundingClientRect();
      inLevel.style.transition = '';

      requestAnimationFrame(() => {
        outLevel.style.transform = `translateX(${outTo})`;
        inLevel.style.transform = 'translateX(0)';
      });

      const finish = () => {
        if (token !== this.levelToken) return; // superseded by a later drill/back
        outLevel.hidden = true;
        outLevel.style.transform = '';
        inLevel.style.transform = '';
      };
      inLevel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 320);

      restoreFocus();
    }

    resetToRoot() {
      // Bump the token so a slideLevels() transitionend/timeout still
      // in flight from a drill/back that was cut short by closing the
      // drawer becomes a no-op instead of re-hiding a level this loop
      // already restored.
      this.levelToken = (this.levelToken || 0) + 1;
      while (this.stack.length > 1) {
        const top = this.stack.pop();
        top.level.hidden = true;
        top.level.style.transform = '';
        if (top.opener) top.opener.setAttribute('aria-expanded', 'false');
      }
      this.stack[0].level.hidden = false;
      this.stack[0].level.style.transform = '';
      this.updateHead();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.transitionToken += 1;
      this.drawer.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('tu-lock');
      // rAF so `hidden` clears before the transition class applies -- same
      // reason as TuMegaNav.open above: one paint has to land between them
      // or the browser skips straight to the end state.
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      document.addEventListener('keydown', this.boundKeydown);
      const first = this.focusables()[0];
      if (first) first.focus();
    }

    close(skipFocusReturn) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.transitionToken += 1;
      const token = this.transitionToken;
      this.drawer.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('tu-lock');
      document.removeEventListener('keydown', this.boundKeydown);
      const finish = () => {
        if (token !== this.transitionToken) return; // reopened before this fired
        this.drawer.hidden = true;
        this.resetToRoot();
      };
      this.panel.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400); // reduced-motion / transition-less fallback
      if (!skipFocusReturn) this.toggle.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      // Tab cycles inside the open drawer only -- `hidden` already keeps a
      // non-current level's rows out of this list for free.
      const items = this.focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // -- TuSearch ---------------------------------------------------------------

  const DEBOUNCE_MS = 250;

  class TuSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('.tu-search__in');
      this.panel = form.querySelector('[data-tu-search-panel]');
      this.status = form.querySelector('[data-tu-search-status]');
      this.closeBtn = form.querySelector('[data-tu-search-close]');
      this.scope = form.querySelector('[data-tu-search-scope]');
      this.baseUrl = form.dataset.tuSearchUrl;
      this.limit = form.dataset.tuSearchLimit || '5';
      if (!this.input || !this.panel || !this.baseUrl) return;

      this.cache = new Map();
      this.controller = null;
      this.sequence = 0;
      this.activeIndex = -1;
      this.debounceTimer = null;
      this.mobileMQ = window.matchMedia('(max-width: 749px)');

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.onFocus());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      // Safari and iOS do not focus a link when it is clicked, so on those
      // browsers a click on a suggestion fires focusout with relatedTarget
      // null, the panel empties itself, and the click lands on nothing
      // (audit 2026-09-04). A pointerdown inside the panel holds the close
      // off long enough for the click to go through.
      this.panel.addEventListener('pointerdown', () => {
        this.holdClose = true;
        clearTimeout(this.holdTimer);
        this.holdTimer = setTimeout(() => { this.holdClose = false; }, 400);
      });
      this.form.addEventListener('focusout', (e) => {
        if (this.holdClose) return;
        if (!this.form.contains(e.relatedTarget)) this.close();
      });
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      window.addEventListener('resize', () => this.measureHeader());
      watchOutside(this.form, () => this.close());
      this.measureHeader();
    }

    measureHeader() {
      const header = document.querySelector('.tu-hdr');
      if (!header) return;
      const bottom = Math.round(header.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--tu-hdr-h', `${bottom}px`);
    }

    query() {
      return this.scope && this.scope.value
        ? `${this.scope.value} AND ${this.input.value}`
        : this.input.value;
    }

    buildUrl(term) {
      const url = new URL(this.baseUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product,query,collection');
      url.searchParams.set('resources[limit]', this.limit);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'tu-predictive-search');
      return url.toString();
    }

    onInput() {
      clearTimeout(this.debounceTimer);
      const value = this.input.value.trim();
      if (!value) {
        this.close();
        return;
      }
      this.debounceTimer = setTimeout(() => this.run(), DEBOUNCE_MS);
    }

    onFocus() {
      if (this.input.value.trim()) this.run();
    }

    onSubmit() {
      // The visible scope select carries no `name`; fold it into `q` right
      // before the browser navigates, so a shopper with no JavaScript still
      // gets an unscoped search and one with JavaScript and a scope chosen
      // gets Empire's own `product_type:X AND terms` mechanism.
      if (this.scope && this.scope.value) {
        this.input.value = `${this.scope.value} AND ${this.input.value}`;
      }
    }

    async run() {
      const term = this.query();
      if (!term) return;
      const seq = ++this.sequence;

      if (this.cache.has(term)) {
        this.render(this.cache.get(term), seq);
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        // 2026-09-14, perf plan A18: three skeleton rows while this fetch is
        // in flight -- render() below always replaces panel.innerHTML (cache
        // hit or real response), and the catch's close() below always clears
        // it on a real failure, so every path this skeleton can take already
        // removes it. An aborted request (a faster keystroke) leaves it up
        // on purpose: the next run() either overwrites it with a fresh copy
        // (the `!querySelector` check below no-ops on the second call) or
        // that call's own render()/close() clears it.
        if (!this.panel.querySelector('.tu-search__skel')) {
          this.panel.innerHTML = '<ul class="tu-search__skel" aria-hidden="true"><li><i></i><i></i></li><li><i></i><i></i></li><li><i></i><i></i></li></ul>';
          this.panel.hidden = false;
        }
        const res = await fetch(this.buildUrl(term), { signal: this.controller.signal });
        const html = await res.text();
        this.cache.set(term, html);
        this.render(html, seq);
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }

    render(html, seq) {
      // A slow early response landing after a later one is exactly the race
      // Empire's own LiveSearch.js has (see melodic-sauteeing-tulip.md); the
      // sequence counter is the fix, on top of aborting the earlier request.
      if (seq !== this.sequence) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const listbox = doc.getElementById('tu-sugg-listbox');
      this.panel.innerHTML = '';
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      if (!listbox) {
        this.close();
        return;
      }
      this.panel.appendChild(listbox);
      this.panel.hidden = false;
      // Same reveal as the Sort menu (tu-system.css "tu-motion"): the panel
      // starts faded and 6px up; the class a frame later lets it settle.
      requestAnimationFrame(() => this.panel.classList.add('is-open'));
      this.input.setAttribute('aria-expanded', 'true');
      if (this.closeBtn) this.closeBtn.hidden = false;
      if (this.mobileMQ.matches) {
        this.measureHeader();
        document.documentElement.classList.add('tu-lock');
      }
      const count = listbox.dataset.tuCount || '0';
      const term = this.input.value.trim();
      if (this.status) {
        this.status.textContent = count === '0'
          ? `No results for "${term}"`
          : `${count} result${count === '1' ? '' : 's'} for "${term}"`;
      }
    }

    options() {
      return Array.from(this.panel.querySelectorAll('[role="option"]'));
    }

    setActive(i) {
      const opts = this.options();
      if (!opts.length) return;
      this.activeIndex = Math.max(0, Math.min(i, opts.length - 1));
      opts.forEach((opt, idx) => opt.setAttribute('aria-selected', idx === this.activeIndex ? 'true' : 'false'));
      const active = opts[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    moveActive(delta) {
      const opts = this.options();
      if (!opts.length) return;
      this.setActive(this.activeIndex < 0 ? (delta > 0 ? 0 : opts.length - 1) : this.activeIndex + delta);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (this.panel.hidden) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.moveActive(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.moveActive(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.setActive(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        this.setActive(this.options().length - 1);
      } else if (e.key === 'Tab') {
        this.close();
      } else if (e.key === 'Enter' && this.activeIndex > -1) {
        e.preventDefault();
        const opts = this.options();
        opts[this.activeIndex].click();
      }
    }

    close() {
      this.panel.classList.remove('is-open');
      this.panel.hidden = true;
      this.panel.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      if (this.closeBtn) this.closeBtn.hidden = true;
      document.documentElement.classList.remove('tu-lock');
    }
  }

  // -- TuNavCompact --------------------------------------------------------
  // Round-two mobile fix 2026-09-15 (F2), client instruction verbatim:
  // "Whenever the screen can't fit the nav doors it becomes hamburger."
  // The inline synchronous script in sections/tu-header.liquid (right
  // after `</nav>`) already makes the FIRST paint correct -- it runs
  // before this file even loads (this one is `defer`), so there is no
  // post-paint class toggle to shift the CLS-sensitive `.tu-nav__bar`
  // (tu-system.css:2103's own comment explains the 0.444-0.449 CLS this
  // file already paid for once, from a font swap; this is the same
  // measurement, just JS-triggered instead of font-triggered, and this
  // is why it must not run late or twice per resize).
  //
  // This only re-measures on resize/orientation change -- a real content
  // reflow that is the direct, expected result of the user's own resize,
  // not the kind of unexpected shift CLS penalises. rAF-debounced so a
  // drag-resize measures once per frame, not once per pixel. Below 750px
  // the CSS floor (tu-system.css:2392) already forces the drawer with no
  // JS involved, so this removes the class rather than measuring there --
  // a door row that only existed above 750 has nothing to overflow below
  // it, and leaving the class on would fight that floor for nothing.
  function measureNavCompact() {
    var nav = document.querySelector('[data-tu-nav]');
    var navIn = nav && nav.querySelector('.tu-nav__in');
    if (!navIn) return;
    var html = document.documentElement;
    if (window.innerWidth < 750) {
      html.classList.remove('tu-nav-compact');
      return;
    }
    // `.tu-nav-compact` hides `.tu-nav` (display: none), which collapses
    // every descendant's scrollWidth/clientWidth to 0 -- so if compact
    // mode is already on, measuring it as-is always reads "0 > 0", i.e.
    // "fits", and would immediately reveal the door row again on the very
    // next resize even though nothing changed. Dropping the class first
    // forces the row back into layout before its width is read; adding it
    // straight back if it still doesn't fit costs one extra reflow but no
    // visible flicker, because both calls happen in this one synchronous
    // function with no `await` between them -- the browser only ever
    // paints the state this function ends on.
    html.classList.remove('tu-nav-compact');
    var needsCompact = navIn.scrollWidth > navIn.clientWidth + 1;
    html.classList.toggle('tu-nav-compact', needsCompact);
  }

  (function bindNavCompactResize() {
    var raf = null;
    window.addEventListener('resize', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measureNavCompact);
    });
  })();

  // -- init ---------------------------------------------------------------

  function init() {
    document.querySelectorAll('[data-tu-nav]').forEach((el) => {
      if (!el.tuBound) {
        el.tuBound = true;
        new TuMegaNav(el);
      }
    });
    document.querySelectorAll('[data-tu-drawer]').forEach((el) => {
      if (!el.tuBound) {
        el.tuBound = true;
        new TuDrawer(el);
      }
    });
    document.querySelectorAll('[data-tu-search]').forEach((el) => {
      if (!el.tuBound) {
        el.tuBound = true;
        new TuSearch(el);
      }
    });
    // Theme-editor re-render (shopify:section:load) can change the menu's
    // own content, so re-measure every time init() runs, not just once.
    measureNavCompact();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
